import {
  ChargeType,
  BillingFrequency,
  ServiceDuration,
  ProductV2,
  ServiceItem,
  PricingModel,
  ServiceItemFormState,
  Pricing,
  PricingTableRow
} from "./types";
import { getEnumTKey } from "@/utils/strings";
import { TFunction } from "i18next";

const TRANSLATION_NAMESPACE = "settings.product";

export const convertPricingTableRowToPricing = (
  row: PricingTableRow
): Pricing => {
  return {
    id: row.id,
    billingFrequency: row.billingFrequency,
    price: row.price,
    convertedPrice: row.convertedPrice || null,
    serviceItemPeriods: row.serviceItemPeriods
  };
};

export const convertPricingToPricingTableRow = (
  pricing: Pricing
): PricingTableRow => {
  return {
    id: pricing.id,
    billingFrequency: pricing.billingFrequency as BillingFrequency | null,
    price: pricing.price ?? 0,
    convertedPrice: pricing.convertedPrice,
    serviceItemPeriods: pricing.serviceItemPeriods as ServiceDuration | null
  };
};

export const getChargeTypeNumber = (
  chargeType: ChargeType | number
): number => {
  if (typeof chargeType === "number") {
    return chargeType === ChargeType.RECURRING ||
      chargeType === ChargeType.ONE_TIME
      ? chargeType
      : ChargeType.RECURRING;
  }
  return chargeType;
};

export const getServiceDurationNumber = (
  serviceDuration: ServiceDuration | null
): number | null => {
  return serviceDuration;
};

export const getChargeTypeFromNumber = (
  chargeTypeNumber: number
): ChargeType => {
  if (
    chargeTypeNumber === ChargeType.RECURRING ||
    chargeTypeNumber === ChargeType.ONE_TIME
  ) {
    return chargeTypeNumber;
  }
  return ChargeType.RECURRING;
};

export const getBillingFrequencyFromNumber = (
  billingFrequencyNumber: number | null
): BillingFrequency | null => {
  if (!billingFrequencyNumber) return null;
  if (
    billingFrequencyNumber >= BillingFrequency.MONTHLY &&
    billingFrequencyNumber <= BillingFrequency.CUSTOM
  ) {
    return billingFrequencyNumber as BillingFrequency;
  }
  return null;
};

export const getServiceDurationFromNumber = (
  serviceDurationNumber: number | null
): ServiceDuration | null => {
  if (!serviceDurationNumber) return null;
  if (
    serviceDurationNumber >= ServiceDuration.ONE_MONTH &&
    serviceDurationNumber <= ServiceDuration.CUSTOM
  ) {
    return serviceDurationNumber as ServiceDuration;
  }
  return null;
};

export const getPricingModelNumber = (
  pricingModel: PricingModel | number
): number => {
  if (typeof pricingModel === "number") {
    return pricingModel >= PricingModel.PER_UNIT &&
      pricingModel <= PricingModel.FLAT_RATE
      ? pricingModel
      : PricingModel.PER_UNIT;
  }
  return pricingModel;
};

export const getPricingModelFromNumber = (
  pricingModelNumber: number | null
): PricingModel | null => {
  if (!pricingModelNumber) return null;
  if (
    pricingModelNumber >= PricingModel.PER_UNIT &&
    pricingModelNumber <= PricingModel.FLAT_RATE
  ) {
    return pricingModelNumber as PricingModel;
  }
  return null;
};

export const getBillingFrequencyTKey = (
  frequency: BillingFrequency
): string => {
  return getEnumTKey(
    TRANSLATION_NAMESPACE,
    "billingFrequency",
    BillingFrequency,
    frequency
  );
};

export const getServiceDurationTKey = (duration: ServiceDuration): string => {
  return getEnumTKey(
    TRANSLATION_NAMESPACE,
    "serviceDuration",
    ServiceDuration,
    duration
  );
};

export const formatBillingFrequency = (
  frequency: BillingFrequency,
  t: TFunction,
  short: boolean = false
): string => {
  const tKey = getEnumTKey(
    TRANSLATION_NAMESPACE,
    short ? "billingFrequencyShort" : "billingFrequency",
    BillingFrequency,
    frequency
  );
  return t(tKey);
};

export const formatPricingModel = (
  pricingModel: PricingModel,
  t: TFunction
): string => {
  const tKey = getEnumTKey(
    TRANSLATION_NAMESPACE,
    "pricingModel",
    PricingModel,
    pricingModel
  );
  return t(tKey);
};

export const formatServiceDuration = (
  duration: ServiceDuration,
  t: TFunction
): string => {
  const tKey = getEnumTKey(
    TRANSLATION_NAMESPACE,
    "serviceDuration",
    ServiceDuration,
    duration
  );
  return t(tKey);
};

export const formatChargeType = (
  chargeType: ChargeType,
  t: TFunction
): string => {
  const tKey = getEnumTKey(
    TRANSLATION_NAMESPACE,
    "chargeType",
    ChargeType,
    chargeType
  );
  return t(tKey);
};

export const formatPrice = (price: number | null): string =>
  `$${(price ?? 0).toLocaleString()}`;

export const formatCompletePricingText = (
  price: number | null,
  pricingModel: number,
  billingFrequency: number | null,
  serviceDuration: number | null,
  t: TFunction,
  chargeType?: number | null
): string => {
  const durationEnum = getServiceDurationFromNumber(serviceDuration);

  // If service duration is custom, return special text
  if (durationEnum === ServiceDuration.CUSTOM) {
    return t(`${TRANSLATION_NAMESPACE}.customPricingAndBilling`);
  }

  const formattedPrice = formatPrice(price);

  const modelEnum = getPricingModelFromNumber(pricingModel);
  const frequencyEnum = getBillingFrequencyFromNumber(billingFrequency);
  const chargeTypeEnum = chargeType
    ? getChargeTypeFromNumber(chargeType)
    : null;

  const isFlatRate = modelEnum === PricingModel.FLAT_RATE;
  const isPerUnit = modelEnum === PricingModel.PER_UNIT;

  let pricingModelText = "";
  if (modelEnum && !isFlatRate) {
    pricingModelText = isPerUnit
      ? t(`${TRANSLATION_NAMESPACE}.utils.perUnit`)
      : formatPricingModel(modelEnum, t);
  }

  let result = pricingModelText
    ? `${formattedPrice} ${pricingModelText}`
    : formattedPrice;

  if (isFlatRate && chargeTypeEnum === ChargeType.ONE_TIME) {
    return result;
  }

  const billedText = t(`${TRANSLATION_NAMESPACE}.billed`);

  if (chargeTypeEnum === ChargeType.ONE_TIME) {
    const onceText = t(
      `${TRANSLATION_NAMESPACE}.billingFrequency.once`
    ).toLowerCase();
    result += `, ${billedText} ${onceText}`;
  } else if (frequencyEnum) {
    if (isPerUnit) {
      const monthSuffix = t(
        `${TRANSLATION_NAMESPACE}.billingFrequencyShort.monthly`
      );
      result += `/${monthSuffix}`;
    } else if (
      !isFlatRate &&
      frequencyEnum !== BillingFrequency.ONCE &&
      frequencyEnum !== BillingFrequency.CUSTOM
    ) {
      const shortFrequencyText = formatBillingFrequency(frequencyEnum, t, true);
      result += `/${shortFrequencyText}`;
    }

    if (frequencyEnum === BillingFrequency.CUSTOM) {
      result += `, ${t(`${TRANSLATION_NAMESPACE}.customBilling`)}`;
    } else {
      result += `, ${billedText} ${formatBillingFrequency(frequencyEnum, t).toLowerCase()}`;
    }
  }

  if (durationEnum) {
    const forText = t(`${TRANSLATION_NAMESPACE}.for`);
    result += `, ${forText} ${formatServiceDuration(durationEnum, t).toLowerCase()}`;
  }

  return result;
};

export const productsToSectionsItems = (
  products: ProductV2[],
  handleEditProduct: (product: ProductV2) => void,
  handleAddServiceItem: (productId: string) => void,
  handleEditServiceItem: (serviceItem: ServiceItem) => void,
  handleDeleteServiceItem: (serviceItem: ServiceItem) => void,
  handleDragEndServiceItems: (result: any) => void
): any[] => {
  if (!products || products.length === 0) {
    return [];
  }

  return [
    {
      isProductV2: true,
      items: products.map((product) => ({
        productId: product.id,
        product: product,
        onEditProduct: () => handleEditProduct(product),
        onAddServiceItem: () => handleAddServiceItem(product.id),
        onEditServiceItem: handleEditServiceItem,
        onDeleteServiceItem: handleDeleteServiceItem,
        onDragEndServiceItems: handleDragEndServiceItems
      }))
    }
  ];
};

export const initializeServiceItemFormData = (
  serviceItem: ServiceItem | null
): ServiceItemFormState => {
  if (!serviceItem) {
    const emptyPricingRow: PricingTableRow = {
      id: `pricing-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      serviceItemPeriods: null,
      billingFrequency: null,
      price: 0,
      convertedPrice: null
    };
    return {
      formData: {
        name: "",
        description: "",
        typeId: 0,
        pricingModel: PricingModel.PER_UNIT,
        chargeType: ChargeType.RECURRING
      },
      pricing: [emptyPricingRow],
      chargeType: ChargeType.RECURRING,
      oneTimePrice: 0
    };
  }

  const chargeType = getChargeTypeFromNumber(
    serviceItem.chargeType as unknown as number
  );
  const pricingModel =
    getPricingModelFromNumber(serviceItem.pricingModel) ||
    PricingModel.PER_UNIT;

  const pricingTableRows = serviceItem.pricing.map((pricing) =>
    convertPricingToPricingTableRow(pricing)
  );

  const oneTimePrice =
    Number(chargeType) === ChargeType.ONE_TIME && serviceItem.pricing.length > 0
      ? (serviceItem.pricing[0].price ?? 0)
      : 0;

  return {
    formData: {
      name: serviceItem.name,
      description: serviceItem.description || "",
      typeId: serviceItem.typeId,
      pricingModel,
      chargeType
    },
    pricing: pricingTableRows,
    chargeType,
    oneTimePrice
  };
};
